import type { ReactNode } from 'react'

/**
 * A deliberately small Markdown subset: headings, lists, quotes, rules and
 * inline bold/italic/code/links. No HTML passthrough — everything is rendered
 * as React elements, so wiki content can never inject markup.
 */

const INLINE = /(\*\*([^*]+)\*\*)|(\*([^*]+)\*)|(`([^`]+)`)|(\[([^\]]+)\]\(([^)\s]+)\))/

function inline(src: string): ReactNode[] {
  const out: ReactNode[] = []
  let rest = src
  let k = 0
  while (rest) {
    const m = rest.match(INLINE)
    if (!m || m.index === undefined) {
      out.push(rest)
      break
    }
    if (m.index > 0) out.push(rest.slice(0, m.index))
    if (m[2]) out.push(<strong key={k++}>{m[2]}</strong>)
    else if (m[4]) out.push(<em key={k++}>{m[4]}</em>)
    else if (m[6]) out.push(<code key={k++}>{m[6]}</code>)
    else if (m[8]) {
      const href = m[9] ?? '#'
      const safe = /^(https?:|mailto:)/i.test(href) ? href : '#'
      out.push(
        <a key={k++} href={safe} target="_blank" rel="noreferrer noopener">
          {m[8]}
        </a>,
      )
    }
    rest = rest.slice(m.index + m[0].length)
  }
  return out
}

export function Markdown({ source }: { source: string }) {
  const lines = (source || '').split('\n')
  const els: ReactNode[] = []
  let i = 0
  let k = 0

  while (i < lines.length) {
    const line = lines[i]

    if (!line.trim()) {
      i++
      continue
    }
    if (/^---+$/.test(line.trim())) {
      els.push(<hr key={k++} />)
      i++
    } else if (line.startsWith('### ')) {
      els.push(<h3 key={k++}>{inline(line.slice(4))}</h3>)
      i++
    } else if (line.startsWith('## ')) {
      els.push(<h3 key={k++}>{inline(line.slice(3))}</h3>)
      i++
    } else if (line.startsWith('# ')) {
      els.push(<h2 key={k++}>{inline(line.slice(2))}</h2>)
      i++
    } else if (line.startsWith('> ')) {
      const buf: string[] = []
      while (i < lines.length && lines[i].startsWith('> ')) buf.push(lines[i++].slice(2))
      els.push(<blockquote key={k++}>{inline(buf.join(' '))}</blockquote>)
    } else if (/^[-*] /.test(line)) {
      const items: ReactNode[] = []
      while (i < lines.length && /^[-*] /.test(lines[i]))
        items.push(<li key={items.length}>{inline(lines[i++].slice(2))}</li>)
      els.push(<ul key={k++}>{items}</ul>)
    } else if (/^\d+\. /.test(line)) {
      const items: ReactNode[] = []
      while (i < lines.length && /^\d+\. /.test(lines[i]))
        items.push(<li key={items.length}>{inline(lines[i++].replace(/^\d+\.\s/, ''))}</li>)
      els.push(<ol key={k++}>{items}</ol>)
    } else {
      els.push(<p key={k++}>{inline(line)}</p>)
      i++
    }
  }

  return (
    <div className="prose" dir="auto">
      {els}
    </div>
  )
}
